import React, { useState, useEffect } from 'react';
import ApiService from '../../services/apiService';
import './HeadlineManager.css';

const HeadlineManager = () => {
  const [headlines, setHeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    text: '',
    date: '',
    isNew: false,
    linkType: 'url',
    linkValue: '',
    pdf: null
  });

  useEffect(() => {
    fetchHeadlines();
  }, []);

  const fetchHeadlines = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getAllHeadlines();
      setHeadlines(response.data || []);
    } catch (error) {
      console.error('Error fetching headlines:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'file' ? files[0] : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const submitData = {
        text: formData.text,
        date: formData.date,
        isNew: formData.isNew,
        linkType: formData.linkType,
        linkValue: formData.linkType === 'url' ? formData.linkValue : '',
        ...(formData.linkType === 'pdf' && formData.pdf && { pdf: formData.pdf })
      };

      await ApiService.createHeadline(submitData);

      await fetchHeadlines();
      resetForm();
    } catch (error) {
      console.error('Error saving headline:', error);
      alert('Error saving headline: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this headline?')) {
      try {
        await ApiService.deleteHeadline(id);
        await fetchHeadlines();
      } catch (error) {
        console.error('Error deleting headline:', error);
        alert('Error deleting headline: ' + error.message);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      text: '',
      date: '',
      isNew: false,
      linkType: 'url',
      linkValue: '',
      pdf: null
    });
    setShowForm(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return <div className="loading">Loading headlines...</div>;
  }

  return (
    <div className="headline-manager">
      <div className="manager-header">
        <button 
          onClick={() => setShowForm(!showForm)} 
          className="btn btn-primary"
        >
          {showForm ? 'Cancel' : 'Add New Headline'}
        </button>
      </div>

      {showForm && (
        <div className="headline-form">
          <h3>Add New Headline</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="text">Headline Text:</label>
              <textarea
                id="text"
                name="text"
                value={formData.text}
                onChange={handleInputChange}
                required
                rows="3"
              />
            </div>

            <div className="form-group">
              <label htmlFor="date">Date:</label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group checkbox-group">
              <label htmlFor="isNew">
                <input
                  type="checkbox"
                  id="isNew"
                  name="isNew"
                  checked={formData.isNew}
                  onChange={handleInputChange}
                />
                Mark as NEW
              </label>
            </div>

            <div className="form-group">
              <label htmlFor="linkType">Link Type:</label>
              <select
                id="linkType"
                name="linkType"
                value={formData.linkType}
                onChange={handleInputChange}
              >
                <option value="url">URL</option>
                <option value="pdf">PDF File</option>
              </select>
            </div>

            {formData.linkType === 'url' && (
              <div className="form-group">
                <label htmlFor="linkValue">URL:</label>
                <input
                  type="url"
                  id="linkValue"
                  name="linkValue"
                  value={formData.linkValue}
                  onChange={handleInputChange}
                  placeholder="https://example.com"
                />
              </div>
            )}

            {formData.linkType === 'pdf' && (
              <div className="form-group">
                <label htmlFor="pdf">PDF File:</label>
                <input
                  type="file"
                  id="pdf"
                  name="pdf"
                  onChange={handleInputChange}
                  accept=".pdf"
                  required
                />
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-success">
                Create Headline
              </button>
              <button type="button" onClick={resetForm} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="headlines-list">
        <h3>Existing Headlines ({headlines.length})</h3>
        <div className="table-container">
          <table className="headlines-table">
            <thead>
              <tr>
                <th>Text</th>
                <th>Date</th>
                <th>Status</th>
                <th>Link Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {headlines.map(headline => (
                <tr key={headline.id}>
                  <td>
                    <div className="headline-text">
                      {headline.is_new && <span className="new-badge">NEW</span>}
                      {headline.text}
                    </div>
                  </td>
                  <td>{formatDate(headline.date)}</td>
                  <td>
                    <span className={`status-badge ${headline.is_active ? 'active' : 'inactive'}`}>
                      {headline.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <span className="link-type">{headline.link_type}</span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        onClick={() => handleDelete(headline.id)}
                        className="btn btn-sm btn-danger"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {headlines.length === 0 && (
                <tr>
                  <td colSpan="5" className="no-data">No headlines found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default HeadlineManager;